<template>
    <div class="camera-stream-view">
        <div class="camera-stream-view__head">
            <h4 class="mb-0">{{ camera.name || ('Camera ' + camera.id) }}</h4>
            <div class="camera-stream-view__meta text-muted">
                <span>{{ streamKind }}</span>
                <span v-if="camera.path" class="camera-stream-view__path" :title="camera.path">
                    {{ shortPath }}
                </span>
            </div>
        </div>

        <div class="camera-stream-view__stage">
            <!-- Same-origin MediaMTX HLS via nginx /mediamtx/ -->
            <iframe
                v-if="useHls"
                class="camera-stream-view__iframe"
                :src="mediamtxPlayerUrl"
                title="Live stream"
                allow="autoplay; fullscreen"
            ></iframe>
            <!-- Browser-native MJPEG / HTTP streams -->
            <img
                v-else-if="isHttpStream && live"
                class="camera-stream-view__img"
                :src="camera.path"
                alt="live stream"
                @error="onNativeError"
            />
            <!-- RTSP / fallback: poll JPEG frames via SDK -->
            <img
                v-else-if="frameSrc"
                class="camera-stream-view__img"
                :src="frameSrc"
                alt="live preview"
            />
            <div v-else class="camera-stream-view__loading">
                {{ errorMsg || 'Connecting…' }}
            </div>
        </div>

        <div class="camera-stream-view__foot">
            <button
                v-if="mediamtxPlayerUrl"
                type="button"
                class="btn btn-border btn-sm me-2"
                @click="toggleHls"
            >
                {{ useHls ? 'JPEG preview' : 'HLS live' }}
            </button>
            <button type="button" class="btn btn-border btn-sm" @click="close">Close</button>
        </div>
    </div>
</template>

<script>
import { bus } from '../../bus';
import axios from 'axios';

export default {
    props: {
        camera: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            live: true,
            useHls: false,
            frameSrc: '',
            errorMsg: '',
            _timer: null,
            _busy: false,
        };
    },
    computed: {
        pathLower() {
            return String(this.camera.path || '').toLowerCase();
        },
        isHttpStream() {
            return this.pathLower.startsWith('http://') || this.pathLower.startsWith('https://');
        },
        /** Same-origin MediaMTX HLS via nginx /mediamtx/. */
        mediamtxPlayerUrl() {
            const p = String(this.camera.path || '');
            const m = p.match(
                /^rtsp:\/\/(?:pcsdk20_mediamtx|host\.docker\.internal|127\.0\.0\.1|localhost)(?::\d+)?\/([^/?#]+)/i
            );
            if (!m) {
                return '';
            }
            const base =
                (window.__MEDIAMTX_HLS_BASE || '').replace(/\/$/, '') || '/mediamtx';
            return `${base}/${encodeURIComponent(m[1])}/?muted=1&autoplay=1`;
        },
        streamKind() {
            if (this.useHls && this.mediamtxPlayerUrl) return 'RTSP → HLS live';
            if (this.pathLower.startsWith('rtsp://')) return 'RTSP (live frames)';
            if (this.isHttpStream) return 'HTTP stream';
            return 'Stream';
        },
        shortPath() {
            const p = String(this.camera.path || '');
            return p.length > 64 ? p.slice(0, 61) + '…' : p;
        },
    },
    methods: {
        close() {
            bus.$emit('openpopup', { open: false });
        },
        toggleHls() {
            this.useHls = !this.useHls;
            if (this.useHls) {
                this.stopPolling();
            } else {
                this.startPolling();
            }
        },
        onNativeError() {
            this.live = false;
            this.errorMsg = '';
            this.startPolling();
        },
        fetchFrame() {
            if (this._busy || !this.camera.id || this.useHls) return;
            this._busy = true;
            axios
                .post(
                    '/cameras/updateCameraPrev',
                    { id: this.camera.id, path: this.camera.path },
                    {
                        timeout: 20000,
                        headers: {
                            'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
                        },
                    }
                )
                .then((response) => {
                    if (response.data && response.data.error) {
                        this.errorMsg = response.data.message || 'No frame';
                        return;
                    }
                    const b64 =
                        typeof response.data === 'string'
                            ? response.data
                            : response.data && response.data.frame;
                    if (!b64) {
                        this.errorMsg = 'No frame from camera';
                        return;
                    }
                    this.errorMsg = '';
                    this.frameSrc =
                        'data:image/jpeg;base64,' + String(b64).replace(/\s+/g, '');
                })
                .catch((err) => {
                    const msg =
                        (err && err.response && err.response.data && err.response.data.message) ||
                        (err && err.message) ||
                        'Preview request failed';
                    this.errorMsg = String(msg);
                })
                .finally(() => {
                    this._busy = false;
                });
        },
        startPolling() {
            this.stopPolling();
            this.fetchFrame();
            this._timer = setInterval(() => this.fetchFrame(), 1500);
        },
        stopPolling() {
            if (this._timer) {
                clearInterval(this._timer);
                this._timer = null;
            }
        },
    },
    mounted() {
        // Default to JPEG frames for RTSP — reliable on local HTTP (MediaMTX HLS uses Secure cookies).
        if (this.isHttpStream && !this.pathLower.startsWith('rtsp://')) {
            this.live = true;
            return;
        }
        this.live = false;
        this.startPolling();
    },
    beforeDestroy() {
        this.stopPolling();
    },
};
</script>

<style lang="scss" scoped>
.camera-stream-view {
    min-width: min(92vw, 880px);
    max-width: 920px;
    padding: 1rem 1.25rem 1.25rem;
    color: #fff;
}
.camera-stream-view__head {
    margin-bottom: 0.75rem;
}
.camera-stream-view__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.8rem;
    margin-top: 0.25rem;
    opacity: 0.75;
}
.camera-stream-view__path {
    word-break: break-all;
}
.camera-stream-view__stage {
    background: #041541;
    border-radius: 6px;
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
}
.camera-stream-view__iframe {
    border: 0;
    width: 100%;
    height: min(70vh, 520px);
    background: #111;
}
.camera-stream-view__img {
    display: block;
    max-width: 100%;
    max-height: 70vh;
    width: auto;
    height: auto;
}
.camera-stream-view__loading {
    padding: 2rem;
    opacity: 0.7;
}
.camera-stream-view__foot {
    margin-top: 0.85rem;
    display: flex;
    justify-content: flex-end;
    align-items: center;
}
.me-2 {
    margin-right: 0.5rem;
}
</style>
